import type { QueryClient } from "@tanstack/react-query";
import { createSessionChannel } from "@a2z/browser-session";
import { inspectSession } from "@/lib/api/client";
import { finishPrivateSessionChange, getPrivateSessionState, invalidatePrivateSession } from "@/lib/api/private-session";
import { firebaseSessionAdapter } from "@/lib/firebase/auth";
export const restaurantSessionChannel = createSessionChannel("restaurant");
export async function resetRestaurantPrivateState(client: QueryClient, generation = getPrivateSessionState().generation) {
  if (generation !== getPrivateSessionState().generation) return;
  await client.cancelQueries();
  if (generation !== getPrivateSessionState().generation) return;
  client.getMutationCache().clear();
  client.removeQueries();
}
export function installRestaurantSessionChannel(client: QueryClient) {
  restaurantSessionChannel.install(() => {
    invalidatePrivateSession();
    void resetRestaurantPrivateState(client);
    void firebaseSessionAdapter.signOut().catch(() => undefined);
  }, async () => {
    const generation = getPrivateSessionState().generation;
    const session = await inspectSession();
    if (generation !== getPrivateSessionState().generation) throw new DOMException("Session changed", "AbortError");
    await resetRestaurantPrivateState(client, generation);
    if (generation !== getPrivateSessionState().generation) throw new DOMException("Session changed", "AbortError");
    finishPrivateSessionChange(session?.seller.storeSlug, session?.seller.id);
    if (session) client.setQueryData(["session"], session);
  });
}

export async function acceptInspectedRestaurantSession(client: QueryClient, session: import("@/lib/api/types").SellerSession, generation: number) {
  const previous = getPrivateSessionState();
  if (previous.generation !== generation || previous.changing) throw new DOMException("Session changed", "AbortError");
  if ((previous.storeSlug && previous.storeSlug !== session.seller.storeSlug) ||
      (previous.actorId && previous.actorId !== session.seller.id)) {
    invalidatePrivateSession();
    const next = getPrivateSessionState().generation;
    await resetRestaurantPrivateState(client, next);
    if (getPrivateSessionState().generation !== next) throw new DOMException("Session changed", "AbortError");
    await firebaseSessionAdapter.signOut().catch(() => undefined);
    if (getPrivateSessionState().generation !== next) throw new DOMException("Session changed", "AbortError");
    finishPrivateSessionChange(session.seller.storeSlug, session.seller.id);
    client.setQueryData(["session"], session);
  }
}
